import { useEffect, useMemo, useState } from "react";
import { useSessionContext } from "@/contexts/SessionContext";
import { useOrganizations } from "@/services/fhir/organization/useOrganizations";
import { organizationTokenFromId } from "../../services/teleconsultoriaScheduleService";
import { cnpjFromOrganizationToken } from "../helpers/affiliationReferences";
import type { AffiliationOrganizationOption } from "../types";

/** Espera o usuário parar de digitar antes de buscar no servidor. */
const SEARCH_DEBOUNCE_IN_MILLISECONDS = 400;
const SEARCH_RESULT_LIMIT = 20;
/** Clínicas e associações (mesmos tipos usados na lista de organizações do legado). */
const CLINIC_ORGANIZATION_TYPES = "prov,bus";

/**
 * Busca de organizações para os diálogos de afiliação.
 * Reutiliza `useOrganizations` do legado (mesmo endpoint e mesmo toast de erro).
 * A própria organização NUNCA aparece (não faz sentido afiliar consigo mesma).
 * `excludeTokens` (opcional): outras organizações a esconder da lista.
 */
export function useAffiliationOrganizationSearch(
  isEnabled: boolean,
  excludeTokens?: Set<string>,
) {
  const { access } = useSessionContext();
  const ownToken = organizationTokenFromId(access?.organizationId);
  const { organizations, loading, fetchOrganizations } = useOrganizations();
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!isEnabled) return;
    const trimmedTerm = searchTerm.trim();
    const timer = window.setTimeout(() => {
      fetchOrganizations({
        active: true,
        count: SEARCH_RESULT_LIMIT,
        type: CLINIC_ORGANIZATION_TYPES,
        ...(trimmedTerm ? { content: trimmedTerm } : {}),
      }).catch(() => undefined);
    }, SEARCH_DEBOUNCE_IN_MILLISECONDS);
    return () => window.clearTimeout(timer);
  }, [isEnabled, searchTerm, fetchOrganizations]);

  const options = useMemo<AffiliationOrganizationOption[]>(
    () =>
      organizations
        .filter((organization) => Boolean(organization.id))
        .filter((organization) => {
          const token = organization.id as string;
          return token !== ownToken && !excludeTokens?.has(token);
        })
        .map((organization) => {
          const token = organization.id as string;
          return {
            token,
            name: organization.name || token,
            cnpj: cnpjFromOrganizationToken(token),
          };
        }),
    [organizations, ownToken, excludeTokens],
  );

  return { options, isSearching: loading, searchTerm, setSearchTerm };
}
